import type { TranslateService } from '@ngx-translate/core';
import type { Options as ComponentOptions } from '@highcharts/dashboards/es-modules/Dashboards/Components/Component.js';
import type { Options as EditableOption } from '@highcharts/dashboards/es-modules/Dashboards/Components/EditableOptions.js';
import {
    dashboardQueryParams,
    isDashboardQueryKey,
} from '@h20/contracts/types/api/dashboard/DashboardQueryCatalog';
import type {
    DashboardQueryKey,
    DashboardQueryParam,
} from '@h20/contracts/types/api/dashboard/DashboardQueryCatalog';
import { authoredQueryKey } from '@h20/contracts/utils/dashboard';
import { translated } from '@h20-shared/utils/translate-utils';

declare module '@highcharts/dashboards/es-modules/Dashboards/Components/EditableOptions.js' {
    interface NestedOptions {
        nameParams?: Readonly<Record<string, string | number>>;
    }
}

/** The settings field that binds a cell to a catalogue query. `name` is an i18n key. */
const DATA_SOURCE_OPTION: EditableOption = {
    name: 'Dashboard.EditMode.Binding.DataSource',
    propertyPath: ['connector', 'id'],
    type: 'select',
};

/** Where a cell keeps what the author typed into its parameter inputs. */
const QUERY_PARAMS_KEY = 'queryParams';

/** Whether a settings field is one this file owns, so installing new ones can replace the old ones. */
export function isDataBindingOption(option: EditableOption): boolean {
    const root = option.propertyPath?.[0];
    return root === 'connector' || root === QUERY_PARAMS_KEY;
}

/**
 * The settings fields that bind a cell to a query. A cell bound to nothing gets the data source field alone.
 */
export function dataBindingOptions(queryKey: DashboardQueryKey | undefined): EditableOption[] {
    if (queryKey === undefined) {
        return [DATA_SOURCE_OPTION];
    }

    const declared = Object.entries(dashboardQueryParams(queryKey));
    return [DATA_SOURCE_OPTION, ...declared.map(([name, param]) => parameterOption(name, param))];
}

/** Rewrites a cell's binding fields for the query it now names, and says whether they changed. */
export function rebuildDataBindingOptions(
    options: ComponentOptions,
    translate: TranslateService
): boolean {
    const current = options.editableOptions ?? [];
    if (!current.some(isDataBindingOption)) {
        return false;
    }

    const rebuilt = resolveLabels(dataBindingOptions(boundQueryKey(options)), translate);
    if (settingsSignature(current.filter(isDataBindingOption)) === settingsSignature(rebuilt)) {
        return false;
    }

    options.editableOptions = [
        ...current.filter((option) => !isDataBindingOption(option)),
        ...rebuilt,
    ];
    return true;
}

/**
 * What a settings panel looks like to the author. Equal signatures mean redrawing the panel would
 * show nothing new.
 */
export function settingsSignature(options: readonly EditableOption[]): string {
    return JSON.stringify(
        options.map((option) => [
            option.type,
            option.propertyPath?.join('.'),
            option.name,
            option.isStandalone,
            // Dashboards writes the board's connector ids into this dropdown as it draws the panel,
            // so a rebuilt field never has them, and comparing them would redraw the panel forever.
            option.propertyPath?.[0] === 'connector'
                ? undefined
                : option.selectOptions?.map((choice) => choice.name),
            option.nestedOptions?.map((group) => [
                group.propertyPath?.join('.'),
                group.name,
                group.showToggle,
                settingsSignature(group.options),
            ]),
        ])
    );
}

/** The same fields, under the labels this app has for their i18n keys. */
export function resolveLabels(
    options: readonly EditableOption[],
    translate: TranslateService
): EditableOption[] {
    return options.map((option) => ({
        ...option,
        name: translated(translate, option.name),
        ...(option.nestedOptions && {
            nestedOptions: option.nestedOptions.map((group) => ({
                ...group,
                name: translated(translate, group.name, group.nameParams),
                options: resolveLabels(group.options, translate),
            })),
        }),
    }));
}

/** Which catalogue query a cell currently names, under the same rule a save compiles by. */
export function boundQueryKey(options: ComponentOptions): DashboardQueryKey | undefined {
    const authored = authoredQueryKey(options);
    return isDashboardQueryKey(authored) ? authored : undefined;
}

/** One parameter's input. A parameter that lists choices gets a dropdown, a boolean gets a switch. */
function parameterOption(name: string, param: DashboardQueryParam): EditableOption {
    const propertyPath = [QUERY_PARAMS_KEY, name];

    if (param.choices !== undefined) {
        return {
            name: param.label,
            propertyPath,
            type: 'select',
            selectOptions: param.choices.map((choice) => ({ name: choice })),
        };
    }

    return { name: param.label, propertyPath, type: param.type === 'boolean' ? 'toggle' : 'input' };
}
